import { clearStore } from "@faro-lotv/project-source";
import { RegistrationEdgeType } from "@faro-lotv/service-wires";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

export enum ScanColoring {
  /** Different color for each scan */
  byScan = "byScan",

  /** The original point cloud color */
  originalColor = "originalColor",

  /** Color scans by connectivity (global and partOf edges) */
  byConnectivity = "byConnectivity",

  /** Grayscale point clouds, imitates a floor plan look.  */
  intensity = "intensity",
}

export enum ScanMarkersVisibilityMode {
  /** Show all the scan markers */
  showAll = "showAll",
  /** Show only visible scan markers */
  showVisible = "showVisible",
  /** hide all scan markers */
  showNone = "showNone",
}

type DataPreparationViewOptionsState = {
  /** Scan coloring to use for displaying multiple individual scans */
  scanColoring: ScanColoring;
  /** ScanMarkers visibility options used for displaying them */
  scanMarkersVisibilityMode: ScanMarkersVisibilityMode;
  /** Whether to show the registration edges. */
  showConnectionLines: boolean;
  /** Whether to color-code the registration edges by their quality. */
  useQualityColorCoding: boolean;
  /** The type of registration edges to show. */
  registrationEdgeType: RegistrationEdgeType | undefined;
  /** Show registration edges which were later discarded by the algorithm or are now invalid. */
  showDiscardedConnectionLines: boolean;
};

/** Initial values for the ViewOptionsSlice */
export const DATA_PREPARATION_VIEW_OPTIONS_INITIAL_VALUES: DataPreparationViewOptionsState =
  {
    scanColoring: ScanColoring.byScan,
    scanMarkersVisibilityMode: ScanMarkersVisibilityMode.showVisible,
    showConnectionLines: true,
    useQualityColorCoding: true,
    registrationEdgeType: undefined,
    showDiscardedConnectionLines: false,
  };

const dataPreparationViewOptionsSlice = createSlice({
  initialState: DATA_PREPARATION_VIEW_OPTIONS_INITIAL_VALUES,
  name: "dataPreparationViewOptions",
  reducers: {
    /**
     * Updates the active scan coloring mode
     *
     * @param state current state
     * @param action new scan coloring mode
     */
    setScanColoring(state, action: PayloadAction<ScanColoring>) {
      state.scanColoring = action.payload;
    },

    /**
     * Updates the active scan markers visibility mode
     *
     * @param state current state
     * @param action new scan markers visibility mode
     */
    setScanMarkersVisibilityMode(
      state,
      action: PayloadAction<ScanMarkersVisibilityMode>,
    ) {
      state.scanMarkersVisibilityMode = action.payload;
    },

    /**
     * Updates whether to show connection lines
     *
     * @param state current state
     * @param action new value for showing connection
     */
    setShowConnectionLines(state, action: PayloadAction<boolean>) {
      state.showConnectionLines = action.payload;
    },

    /**
     * Updates whether to use quality color coding when rendering the edges
     *
     * @param state current state
     * @param action new value for quality color coding
     */
    setQualityColorCoding(state, action: PayloadAction<boolean>) {
      state.useQualityColorCoding = action.payload;
    },

    /**
     * Updates the RegistrationEdgeType to use for displaying the registration results
     *
     * @param state current state
     * @param action new value for global or local results
     */
    setRegistrationEdgeType(
      state,
      action: PayloadAction<RegistrationEdgeType>,
    ) {
      state.registrationEdgeType = action.payload;
    },

    /**
     * Updates whether to show discarded connection lines.
     *
     * @param state current state
     * @param action new value for showing discarded connections
     */
    setShowDiscardedConnectionLines(state, action: PayloadAction<boolean>) {
      state.showDiscardedConnectionLines = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      clearStore,
      () => DATA_PREPARATION_VIEW_OPTIONS_INITIAL_VALUES,
    );
  },
});

export const {
  setScanColoring,
  setScanMarkersVisibilityMode,
  setShowConnectionLines,
  setQualityColorCoding,
  setRegistrationEdgeType,
  setShowDiscardedConnectionLines,
} = dataPreparationViewOptionsSlice.actions;

export const dataPreparationViewOptionsReducer =
  dataPreparationViewOptionsSlice.reducer;
